declare global {
  interface Window {
    google?: typeof google;
  }
}

export interface GoogleMapsConfig {
  places_enabled: boolean;
  maps_api_key: string | null;
}

let loaderPromise: Promise<typeof google> | null = null;
let cachedConfig: GoogleMapsConfig | null = null;

/** Test-only: clear in-memory Maps loader state. */
export function resetGoogleMapsLoader(): void {
  loaderPromise = null;
  cachedConfig = null;
}

export async function fetchGoogleMapsConfig(
  request: () => Promise<{ places_enabled?: boolean; maps_api_key?: string | null }>,
): Promise<GoogleMapsConfig> {
  if (cachedConfig) return cachedConfig;
  const envKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();
  if (envKey) {
    cachedConfig = { places_enabled: true, maps_api_key: envKey };
    return cachedConfig;
  }
  try {
    const data = await request();
    const key = data.maps_api_key?.trim() || null;
    cachedConfig = {
      places_enabled: Boolean(data.places_enabled && key),
      maps_api_key: key,
    };
  } catch {
    return { places_enabled: false, maps_api_key: null };
  }
  return cachedConfig;
}

async function attachPlacesLibrary(): Promise<typeof google> {
  const g = window.google;
  if (!g?.maps) {
    throw new Error('Google Maps failed to load');
  }
  const importer = g.maps.importLibrary;
  if (typeof importer === 'function') {
    await importer.call(g.maps, 'places');
  }
  if (!g.maps.places) {
    throw new Error('Google Places failed to load');
  }
  return g;
}

export function loadGoogleMaps(apiKey: string): Promise<typeof google> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is browser-only'));
  }
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<typeof google>((resolve, reject) => {
    const finish = () => {
      void attachPlacesLibrary().then(resolve).catch(reject);
    };

    const existing = document.querySelector('script[data-bilt-google-maps="1"]');
    if (existing) {
      if ((existing as HTMLScriptElement).dataset.biltGoogleMapsLoaded === '1') {
        finish();
        return;
      }
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => reject(new Error('Google Maps failed to load')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.dataset.biltGoogleMaps = '1';
    // Do not use loading=async: that fires script.onload before Places is attached.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.biltGoogleMapsLoaded = '1';
      finish();
    };
    script.onerror = () => reject(new Error('Google Maps failed to load'));
    document.head.appendChild(script);
  }).catch((err) => {
    loaderPromise = null;
    throw err;
  });

  return loaderPromise;
}

export function extractCityState(components: google.maps.GeocoderAddressComponent[] | undefined): {
  city: string | null;
  state: string | null;
} {
  let city: string | null = null;
  let state: string | null = null;
  for (const component of components ?? []) {
    const types = component.types ?? [];
    if (types.includes('locality') && !city) {
      city = component.long_name;
    } else if (types.includes('postal_town') && !city) {
      city = component.long_name;
    } else if (types.includes('administrative_area_level_1') && !state) {
      state = component.short_name || component.long_name;
    }
  }
  return { city, state };
}
